import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { ApiError } from '../../api/client';
import type { EmailActionView, EmailPasswordCompleteRequest, EmailRegistrationCompleteRequest, Envelope } from '../../api/contract';
import { captureAuthEpoch } from '../../app/auth-context';
import { authMutation, createAuthRequestKey, emailActionGet } from './api';
import { safeReturnTo } from './returnTo';
import './auth.css';

type PrepareLink = (context: { actionId: string; phase: 'confirm' | 'complete'; replay?: true }) => Promise<boolean>;
type Entry = { proof?: string; actionId?: string; purpose: EmailActionView['purpose']; returnTo: string; invalid: boolean };

function readEntry(): Entry {
  const url = new URL(window.location.href);
  const fragment = url.hash.slice(1);
  // This happens during the initial render, before any effect/API dispatch.
  // Neither React/router history state nor browser storage receives the proof.
  if (url.hash) { url.hash = ''; window.history.replaceState(window.history.state, '', url); }
  const actionId = url.searchParams.get('actionId') ?? undefined;
  const purpose = url.pathname.endsWith('/reset') ? 'RESET_PASSWORD' : url.pathname.endsWith('/link') ? 'LINK_EMAIL' : 'REGISTER';
  return { purpose, returnTo: safeReturnTo(url.searchParams.get('returnTo')), ...(fragment ? { proof: fragment } : {}), ...(actionId ? { actionId } : {}), invalid: Boolean(fragment && !/^[A-Za-z0-9_-]{43}$/u.test(fragment)) || (!fragment && !actionId) };
}

function failureText(error: unknown): string {
  if (!(error instanceof ApiError)) return error instanceof Error ? error.message : 'Не удалось выполнить действие. Повторите попытку.';
  if (error.status === 0) return 'Не удалось подтвердить результат: соединение прервано. Повторите то же действие.';
  if (error.status === 429) return 'Слишком много попыток. Дождитесь, когда кнопка станет доступна.';
  if (error.status === 503) return 'Вход по почте временно недоступен. Попробуйте позже.';
  if (error.code === 'EMAIL_ACTION_CONTEXT_CHANGED') return 'В другой вкладке открыто другое письмо. Вернитесь к нужному письму и откройте его ссылку заново.';
  if (error.code === 'AUTH_EPOCH_CHANGED') return 'Аккаунт изменился в другой вкладке. Откройте ссылку из письма заново.';
  if (error.status === 410) return 'Ссылка недействительна, уже использована или срок её действия истёк. Запросите новое письмо.';
  if (error.status === 401) return 'Срок подтверждения истёк или этот браузер не сохранил подтверждение. Откройте ссылку из письма заново.';
  if (error.status === 403) return 'Не удалось подтвердить этот аккаунт. Проверьте вход через Яндекс ID и откройте исходное письмо заново.';
  return error.message;
}

// Public proof/setup UI: deliberately has no financial provider or /me lookup.
// T14 supplies session preparation/targeted reauth through explicit clicks only.
export function EmailActionPage({ prepareLink, onLinkComplete, onLinkFailure, linkRecovery }: { prepareLink?: PrepareLink; onLinkComplete?: () => Promise<void>; onLinkFailure?: (error: unknown) => void; linkRecovery?: ReactNode }) {
  const [entry] = useState(readEntry);
  const [epoch] = useState(captureAuthEpoch);
  const [view, setView] = useState<EmailActionView>();
  const [pending, setPending] = useState(true);
  const [error, setError] = useState('');
  const [terminal, setTerminal] = useState(false);
  const [done, setDone] = useState(false);
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const passwordInput = useRef<HTMLInputElement>(null);
  const loading = useRef(false);
  const active = useRef(false);
  const submitting = useRef(false);
  const proof = useRef(entry.proof);
  const expectedId = useRef(entry.actionId);
  const openKey = useRef<string | undefined>(undefined);
  const confirmKey = useRef<string | undefined>(undefined);
  const completion = useRef<{ password?: string; key: string } | undefined>(undefined);
  const nativeRegistration = view?.purpose === 'REGISTER' && view.passwordRequired === false;

  const fail = (caught: unknown) => {
    setError(failureText(caught));
    if (caught instanceof ApiError) {
      if ([401, 403, 410].includes(caught.status) || ['EMAIL_ACTION_CONTEXT_CHANGED', 'AUTH_EPOCH_CHANGED'].includes(caught.code)) { setTerminal(true); setPassword(''); completion.current = undefined; }
      if (caught.retryAfterMs !== undefined) setRetryAt(Date.now() + caught.retryAfterMs);
    }
  };

  const load = async () => {
    if (loading.current || retryAt > Date.now()) return;
    if (entry.invalid) { setError('Ссылка недействительна. Откройте полную ссылку из письма.'); setPending(false); setTerminal(true); return; }
    loading.current = true; setPending(true); setError('');
    try {
      let result: Envelope<EmailActionView>;
      if (proof.current) {
        openKey.current ??= createAuthRequestKey();
        result = await authMutation<Envelope<EmailActionView>>('/auth/web/email/actions/open', { proof: proof.current }, openKey.current, epoch);
      } else {
        result = await emailActionGet<Envelope<EmailActionView>>(expectedId.current!, epoch);
      }
      if (!active.current) return;
      if ((expectedId.current && result.data.actionId !== expectedId.current) || result.data.purpose !== entry.purpose) throw new ApiError(409, 'EMAIL_ACTION_CONTEXT_CHANGED', 'Контекст письма изменился');
      expectedId.current = result.data.actionId;
      proof.current = undefined;
      const url = new URL(window.location.href); url.searchParams.set('actionId', result.data.actionId);
      window.history.replaceState(window.history.state, '', url);
      setView(result.data);
    } catch (caught) { if (active.current) fail(caught); }
    finally { loading.current = false; if (active.current) setPending(false); }
  };

  useEffect(() => {
    active.current = true;
    queueMicrotask(() => { if (active.current) void load(); });
    return () => { active.current = false; };
    // Entry is immutable; reload retries are explicit and never auto-confirm.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { document.title = `${entry.purpose === 'RESET_PASSWORD' ? 'Сброс пароля' : 'Подтверждение почты'} — Казна`; }, [entry.purpose]);
  useEffect(() => {
    if (!retryAt) return;
    const timeout = window.setTimeout(() => setRetryAt(0), Math.min(Math.max(0, retryAt - Date.now()), 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [retryAt]);

  const submit = async (phase: 'confirm' | 'complete') => {
    if (!view || submitting.current || terminal || retryAt > Date.now()) return;
    const passwordPoints = Array.from(password);
    if (phase === 'complete' && !nativeRegistration && (passwordPoints.length < 15 || passwordPoints.length > 128 || /[\uD800-\uDFFF]/u.test(password))) {
      setInvalid(true); setError('Пароль должен содержать 15–128 символов Unicode. Пробелы сохраняются.'); passwordInput.current?.focus(); return;
    }
    submitting.current = true; setPending(true); setError('');
    let completionSent = false;
    try {
      const replay = phase === 'complete' && uncertain && completion.current?.password === password;
      if (view.purpose === 'LINK_EMAIL' && (!prepareLink || !await prepareLink({ actionId: view.actionId, phase, ...(replay ? { replay: true } : {}) }))) {
        if (active.current) setError('Для привязки почты требуется подготовить вход и подтвердить аккаунт через Яндекс ID.');
        return;
      }
      if (!active.current) return;
      if (phase === 'confirm') {
        confirmKey.current ??= createAuthRequestKey();
        await authMutation('/auth/web/email/actions/confirm', { actionId: view.actionId }, confirmKey.current, epoch);
        if (active.current) setView({ ...view, state: 'EMAIL_VERIFIED' });
      }
      // One explicit native confirmation also completes registration. Once
      // confirmed, retries only complete; a lost confirm retains its own key.
      if (phase === 'complete' || nativeRegistration) {
        if (!active.current) return;
        const completionPassword = nativeRegistration ? undefined : password;
        if (!completion.current || completion.current.password !== completionPassword) completion.current = { ...(nativeRegistration ? {} : { password }), key: createAuthRequestKey() };
        const path = view.purpose === 'RESET_PASSWORD' ? '/auth/web/email/password-reset/complete' : view.purpose === 'LINK_EMAIL' ? '/me/identities/email/link/complete' : '/auth/web/email/registration/complete';
        const body: EmailRegistrationCompleteRequest | EmailPasswordCompleteRequest = nativeRegistration ? { actionId: view.actionId } : { actionId: view.actionId, password };
        completionSent = true;
        await authMutation(path, body, completion.current.key, epoch);
        completionSent = false;
        if (active.current) { setDone(true); setPassword(''); completion.current = undefined; }
        if (view.purpose === 'LINK_EMAIL') await onLinkComplete?.();
      }
      if (active.current) setUncertain(false);
    } catch (caught) {
      if (view.purpose === 'LINK_EMAIL') onLinkFailure?.(caught);
      if (active.current) { if (caught instanceof ApiError && caught.status === 0 && completionSent) setUncertain(true); fail(caught); }
    } finally { submitting.current = false; if (active.current) setPending(false); }
  };
  const showPassword = view && !nativeRegistration && (view.purpose === 'RESET_PASSWORD' || view.state === 'EMAIL_VERIFIED');
  const title = done ? (entry.purpose === 'RESET_PASSWORD' ? 'Пароль изменён' : 'Почта подтверждена') : entry.purpose === 'RESET_PASSWORD' ? 'Новый пароль' : entry.purpose === 'LINK_EMAIL' ? 'Привязать почту' : 'Подтвердить почту';
  const loginPath = `/auth/email/login${entry.returnTo === '/dashboard' ? '' : `?returnTo=${encodeURIComponent(entry.returnTo)}`}`;
  const formSubmit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); void submit('complete'); };

  return <main className="login-page public-auth-page email-action-page">
    <section className="login-card" aria-labelledby="email-action-title" aria-busy={pending}>
      <img className="brand-mark large" src="/icon.svg" alt="" aria-hidden="true" />
      <h1 id="email-action-title">{title}</h1>
      {done ? <>
        <p role="status">{nativeRegistration ? 'Почта подтверждена. Вернитесь в приложение и войдите с созданным паролем.' : entry.purpose === 'RESET_PASSWORD' ? 'Войдите с новым паролем.' : 'Пароль сохранён. Теперь можно войти по почте.'}</p>
        <Link className="button button-primary wide" to={entry.purpose === 'LINK_EMAIL' ? '/account/sign-in-methods' : loginPath}>{entry.purpose === 'LINK_EMAIL' ? 'К способам входа' : 'Войти по почте'}</Link>
        <a className="button button-secondary wide" href="kazna://auth/email/complete">Вернуться в приложение</a>
      </> : <>
        {view && <p className="email-action-address">{view.maskedEmail}</p>}
        {!view && pending && <p role="status">Открываем письмо…</p>}
        {error && <p id="email-action-error" className="error" role="alert">{error}</p>}
        {linkRecovery}
        {!terminal && !done && view && (showPassword ? <form className="form" noValidate onSubmit={formSubmit}>
          <p>Придумайте пароль: от 15 до 128 символов. Пробелы тоже считаются.</p>
          {entry.purpose === 'LINK_EMAIL' && <p>Если потребуется подтверждение через Яндекс ID, после возвращения введите пароль снова.</p>}
          <div className="field">
            <label htmlFor="email-new-password">Пароль</label>
            <div className="auth-password">
              <input ref={passwordInput} id="email-new-password" name="new-password" type={visible ? 'text' : 'password'} autoComplete="new-password" required
                value={password} readOnly={pending || uncertain} aria-invalid={invalid} aria-describedby={error ? 'email-action-error' : undefined}
                onChange={event => { setPassword(event.target.value); setInvalid(false); completion.current = undefined; }} />
              <button className="button button-secondary" type="button" aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'} aria-pressed={visible} onClick={() => setVisible(value => !value)}>{visible ? <EyeOff size={20} /> : <Eye size={20} />}</button>
            </div>
          </div>
          <button className="button button-primary wide" type="submit" disabled={pending || retryAt > 0}>{pending ? 'Сохраняем…' : uncertain ? 'Повторить сохранение' : 'Сохранить пароль'}</button>
        </form> : <>
          <p>{nativeRegistration ? 'Подтвердите, что эта почта принадлежит вам.' : 'Подтвердите, что эта почта принадлежит вам. Затем задайте пароль.'}</p>
          <button className="button button-primary wide" type="button" disabled={pending || retryAt > 0} onClick={() => void submit(nativeRegistration && view.state === 'EMAIL_VERIFIED' ? 'complete' : 'confirm')}>{pending ? 'Подтверждаем…' : nativeRegistration && uncertain ? 'Повторить подтверждение' : nativeRegistration && view.state === 'EMAIL_VERIFIED' ? 'Завершить подтверждение' : 'Подтвердить почту'}</button>
        </>)}
        {!view && !terminal && !pending && <button className="button button-primary wide" disabled={retryAt > 0} onClick={() => void load()}>Повторить открытие</button>}
        {retryAt > 0 && <p role="status">Попробуйте ещё раз после окончания паузы.</p>}
        {terminal && entry.purpose !== 'LINK_EMAIL' && <Link className="button button-primary wide" to={entry.purpose === 'RESET_PASSWORD' ? '/auth/email/reset' : '/auth/email/register'}>Запросить новое письмо</Link>}
        <Link className="button button-secondary wide" to={loginPath}>Войти по почте</Link>
      </>}
    </section>
  </main>;
}
